import { z } from 'zod';

/**
 * Készlet — a **kifelé** látható alak (ADR-145, `D` üzleti modell).
 *
 * A belső készlet-modell (`@kgc/inventory`) darab-szintű: az `InventoryItem`-en
 * `serialNumber`, `warehouseId`, `status` van, `9` értékű `InventoryStatus`-szal.
 * Kifelé ebből **a mennyiség és az elérhetőség** tartozik — a többi üzemeltetési kép.
 *
 * ⛔ Ez a fájl **nem importálja** a belső csomagot, és nem is fogja: az
 * `@kgc/api-contract` a függőségi gráf **levele** (`dependencies: ["zod"]`), és ezt a
 * kontraktus-fogyasztók miatt meg kell őrizni — a web és egy idegen ERP adaptere
 * ezt húzza be, nem a teljes inventory-t. A leképezés iránya fordított: a **belső**
 * oldal függ a kontraktustól (`@kgc/inventory` → `mappers/to-contract.ts`).
 *
 * @see ADR-145 — a külső alak explicit mapper, sosem re-export
 */

/**
 * Készlet-elérhetőség kifelé — az `InventoryStatus` `9` értékének szűkítése.
 *
 * A belső státusz üzemeltetési kép: a `DAMAGED` · `LOST` · `SCRAPPED` · `IN_SERVICE` ·
 * `IN_TRANSIT` azt árulná el egy publikus vevőnek, mennyi a törött, mi veszett el, mi
 * van szervizben. Ezért a leképezés **whitelist**: ami nincs a táblában, az nem megy ki.
 */
export const PublicStockStatusSchema = z.enum(['AVAILABLE', 'RESERVED', 'NOT_AVAILABLE']);
export type PublicStockStatus = z.infer<typeof PublicStockStatusSchema>;

/**
 * Készlet-szint egy cikkre — aggregált.
 *
 * A `availableQuantity` és a `stockLevelStatus` a belső oldalon **már létezik**
 * (`@kgc/inventory` `inventory.dto.ts`), tehát ez átemelés, nem új számítás.
 */
export const StockLevelSchema = z.object({
  productId: z.string().uuid(),
  availableQuantity: z.number().int().nonnegative(),
  status: PublicStockStatusSchema,
});
export type StockLevel = z.infer<typeof StockLevelSchema>;
